import { useState } from "react";
import { MapPin, Target, BarChart3, Activity, Building2, Handshake } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { StatCard } from "@/components/analytics/StatCard";
import { OwnershipChart } from "@/components/analytics/OwnershipChart";
import { SportTypeChart } from "@/components/analytics/SportTypeChart";
import { SourceDistributionChart } from "@/components/analytics/SourceDistributionChart";
import { ConfidenceHistogram } from "@/components/analytics/ConfidenceHistogram";
import { ScanHistoryTable } from "@/components/analytics/ScanHistoryTable";

export function AnalyticsPage() {
  const [selectedScan, setSelectedScan] = useState<string>("all");

  const { data: stats, isLoading: statsLoading } =
    trpc.analytics.locationStats.useQuery({
      scanId: selectedScan === "all" ? undefined : selectedScan,
    });
  const { data: history } = trpc.analytics.scanHistory.useQuery({ limit: 50 });

  const avgConfidence =
    stats?.confidenceBuckets && stats.sportFieldCount > 0
      ? (() => {
          let sum = 0;
          let total = 0;
          for (const [i, bucket] of stats.confidenceBuckets.entries()) {
            const midpoint = (i * 10 + 5) / 100;
            sum += midpoint * bucket.count;
            total += bucket.count;
          }
          return total > 0 ? Math.round((sum / total) * 100) : 0;
        })()
      : 0;

  const totalScans = history?.length ?? 0;

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Analytics</h1>
        <Select value={selectedScan} onValueChange={setSelectedScan}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All scans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scans</SelectItem>
            {history?.map((scan) => (
              <SelectItem key={scan.id} value={scan.id}>
                {scan.configName || scan.id.slice(0, 8)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {statsLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          Loading analytics...
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <StatCard
              icon={MapPin}
              label="Total Locations"
              value={stats?.totalLocations ?? 0}
            />
            <StatCard
              icon={Target}
              label="Sport Fields Found"
              value={stats?.sportFieldCount ?? 0}
              subtext={
                stats && stats.totalLocations > 0
                  ? `${Math.round((stats.sportFieldCount / stats.totalLocations) * 100)}% of total`
                  : undefined
              }
            />
            <StatCard
              icon={BarChart3}
              label="Avg Confidence"
              value={`${avgConfidence}%`}
            />
            <StatCard
              icon={Activity}
              label="Total Scans"
              value={totalScans}
            />
            <StatCard
              icon={Building2}
              label="City Approved"
              value={stats?.cityApprovedCount ?? 0}
              subtext={
                stats && stats.totalLocations > 0
                  ? `${Math.round(((stats.cityApprovedCount ?? 0) / stats.totalLocations) * 100)}% of total`
                  : undefined
              }
            />
            <StatCard
              icon={Handshake}
              label="Sponsor Approved"
              value={stats?.sponsorApprovedCount ?? 0}
              subtext={
                stats && stats.totalLocations > 0
                  ? `${Math.round(((stats.sponsorApprovedCount ?? 0) / stats.totalLocations) * 100)}% of total`
                  : undefined
              }
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <ErrorBoundary name="SportTypeChart">
              <Card>
                <CardHeader>
                  <CardTitle>Sport Type Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <SportTypeChart data={stats?.sportTypeDistribution ?? []} />
                </CardContent>
              </Card>
            </ErrorBoundary>

            <ErrorBoundary name="SourceDistributionChart">
              <Card>
                <CardHeader>
                  <CardTitle>Source Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <SourceDistributionChart data={stats?.sourceDistribution ?? []} />
                </CardContent>
              </Card>
            </ErrorBoundary>
          </div>

          <ErrorBoundary name="OwnershipChart">
            <Card>
              <CardHeader>
                <CardTitle>Ownership Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <OwnershipChart data={stats?.ownershipDistribution ?? []} />
              </CardContent>
            </Card>
          </ErrorBoundary>

          <ErrorBoundary name="ConfidenceHistogram">
            <Card>
              <CardHeader>
                <CardTitle>Confidence Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ConfidenceHistogram data={stats?.confidenceBuckets ?? []} />
              </CardContent>
            </Card>
          </ErrorBoundary>

          <ErrorBoundary name="ScanHistory">
            <Card>
              <CardHeader>
                <CardTitle>Scan History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScanHistoryTable data={history ?? []} />
              </CardContent>
            </Card>
          </ErrorBoundary>
        </>
      )}
    </div>
  );
}

export default AnalyticsPage;
